import type {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';

declare global {
  interface Global {
    _firestoreEmulatorConnected?: boolean;
  }
}

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Flags to track emulator initialization
let authEmulatorInitialized = false;
let databaseEmulatorInitialized = false;
let storageEmulatorInitialized = false;
let functionsEmulatorInitialized = false;
let cachedApp: import('firebase/app').FirebaseApp | null = null;
let cachedFirestore: import('firebase/firestore').Firestore | null = null;
let cachedFunctions: import('firebase/functions').Functions | null = null;

const initializeFirebase = async () => {
  if (cachedApp) return cachedApp;
  const { initializeApp } = await import('firebase/app');
  cachedApp = initializeApp(firebaseConfig);
  return cachedApp;
};

export const getFunctionsService = async () => {
  if (cachedFunctions) return cachedFunctions;
  const { getFunctions, connectFunctionsEmulator } = await import(
    'firebase/functions'
  );
  const app = await initializeFirebase();
  const functions = getFunctions(app, 'us-central1');

  if (import.meta.env.DEV && !functionsEmulatorInitialized) {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001); // ✅ this must come right after getFunctions
    functionsEmulatorInitialized = true;
  }
  cachedFunctions = functions;
  return functions;
};

export const getFirestoreInstance = async () => {
  if (cachedFirestore) return cachedFirestore;

  const { getFirestore, connectFirestoreEmulator } = await import(
    'firebase/firestore'
  );
  const app = await initializeFirebase();
  const firestore = getFirestore(app);

  if (import.meta.env.DEV || process.env.NODE_ENV === 'development') {
    // Prevent reconnecting on hot reload
    if (!(globalThis as Global)._firestoreEmulatorConnected) {
      connectFirestoreEmulator(firestore, '127.0.0.1', 8080); // change port if needed
      (globalThis as Global)._firestoreEmulatorConnected = true;
    }
  }

  cachedFirestore = firestore;
  return firestore;
};

export async function getAuthService() {
  const {
    getAuth,
    connectAuthEmulator,
    setPersistence,
    browserLocalPersistence,
    onAuthStateChanged,
  } = await import('firebase/auth');

  const app = await initializeFirebase();
  const auth = getAuth(app);

  if (import.meta.env.MODE === 'development' && !authEmulatorInitialized) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099');
    authEmulatorInitialized = true;
  }

  await setPersistence(auth, browserLocalPersistence);

  onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('User is signed in:', user);
    } else {
      console.log('No user is signed in.');
    }
  });

  return auth;
}
/**
 * Dynamically import Realtime Database
 */
export async function getDatabaseService() {
  const { getDatabase, connectDatabaseEmulator } = await import(
    'firebase/database'
  );
  const app = await initializeFirebase();
  const rtdb = getDatabase(app);

  if (import.meta.env.MODE === 'development' && !databaseEmulatorInitialized) {
    connectDatabaseEmulator(rtdb, '127.0.0.1', 9000);
    databaseEmulatorInitialized = true;
  }
  return rtdb;
}

/**
 * Dynamically import Storage
 */
export async function getStorageService() {
  const { getStorage, connectStorageEmulator } = await import(
    'firebase/storage'
  );
  const app = await initializeFirebase();
  const storage = getStorage(app);

  if (import.meta.env.MODE === 'development' && !storageEmulatorInitialized) {
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    storageEmulatorInitialized = true;
  }
  return storage;
}

export const firestoreConverter = <T extends object>(): FirestoreDataConverter<
  T,
  T
> => ({
  // Convert user-defined object to Firestore format
  toFirestore: (data: T): T => {
    return { ...data };
  },

  // Convert Firestore snapshot back to the correct object type
  fromFirestore: (
    snapshot: QueryDocumentSnapshot<T>,
    options: SnapshotOptions
  ): T => {
    return { id: snapshot.id, ...snapshot.data(options) } as T;
  },
});
